(() => {
  const root = document.querySelector('[data-public-catalog]');
  if (!root || root.dataset.ready === 'true') return;
  root.dataset.ready = 'true';

  const endpoint = root.dataset.catalogEndpoint;
  const searchInput = root.querySelector('[data-catalog-search]');
  const navigation = root.querySelector('[data-catalog-nav]');
  const status = root.querySelector('[data-catalog-status]');
  const feature = root.querySelector('[data-catalog-feature]');
  const shelves = root.querySelector('[data-catalog-shelves]');
  const dialog = root.querySelector('[data-catalog-dialog]');
  const detail = root.querySelector('[data-catalog-detail]');
  const closeButton = root.querySelector('[data-catalog-close]');
  let items = [];
  let activeSection = 'home';
  let featureItems = [];
  let featureIndex = 0;
  let featureTimer = null;
  let featurePaused = false;

  const featureInterval = 10000;

  const roundUrl = (item) => `${window.location.pathname}?round=${encodeURIComponent(item.slug || item.id)}`;

  const categoryFor = (item) => {
    const category = item.taxonomyPath?.find((entry) => entry.type === 'category')
      || item.taxonomyPath?.find((entry) => entry.type === 'group');
    return category?.title || item.contentKind || 'Trivia';
  };

  const isGeneral = (item) => String(item.contentKind || '').trim().toLowerCase() === 'general';
  const isThemed = (item) => ['themed', 'holiday'].includes(String(item.contentKind || '').trim().toLowerCase());
  const isNonGeneral = (item) => !isGeneral(item);
  const sectionItems = (section) => section === 'general' ? items.filter(isGeneral) : section === 'themed' ? items.filter(isThemed) : items;
  const searchableText = (item) => [item.title, item.topicName, item.seriesName, item.topicType, item.description, item.difficulty, item.gameStyle, ...((item.taxonomyPath || []).map((entry) => entry.title))]
    .map((value) => String(value || '').toLowerCase())
    .join(' ');
  const publishedAt = (item) => new Date(item.publishedAt || item.updatedAt || 0).getTime();
  const newestFirst = (left, right) => publishedAt(right) - publishedAt(left) || String(left.title || '').localeCompare(String(right.title || ''));
  const popularFirst = (left, right) => (Number(left.popularityRank || Number.MAX_SAFE_INTEGER) - Number(right.popularityRank || Number.MAX_SAFE_INTEGER)) || newestFirst(left, right);

  const imageFor = (item, placement) => {
    const requested = item.images?.[placement]?.url;
    return requested || item.images?.vertical?.url || item.images?.horizontal?.url || item.images?.square?.url || '';
  };

  const accentFor = (item) => {
    const colors = ['#b33b32', '#276b5a', '#315f8a', '#9a6a20', '#674c82', '#9a4f68', '#3e6c7d'];
    const seed = `${item.topicName || ''}${item.seriesName || ''}${item.id}`;
    const index = Array.from(seed).reduce((total, character) => total + character.charCodeAt(0), 0) % colors.length;
    return colors[index];
  };

  const setText = (element, value) => {
    element.textContent = value || '';
    return element;
  };

  const makeCover = (item, placement = 'vertical', large = false) => {
    const cover = document.createElement('div');
    const imageUrl = imageFor(item, placement);
    cover.className = `slt-public-cover${large ? ' slt-public-cover--large' : ''}${imageUrl ? ' has-image' : ''}`;
    cover.style.setProperty('--catalog-accent', accentFor(item));
    if (imageUrl) cover.style.backgroundImage = `url(${JSON.stringify(imageUrl)})`;
    if (large && placement === 'vertical') {
      const desktopImage = imageFor(item, 'vertical');
      const mobileImage = imageFor(item, 'horizontal');
      if (desktopImage) cover.style.setProperty('--catalog-image-desktop', `url(${JSON.stringify(desktopImage)})`);
      if (mobileImage) cover.style.setProperty('--catalog-image-mobile', `url(${JSON.stringify(mobileImage)})`);
    }

    const shade = document.createElement('span');
    shade.className = 'slt-public-cover__shade';
    cover.append(shade);

    if (item.isPopular) {
      const popular = setText(document.createElement('span'), 'Popular');
      popular.className = 'slt-public-popular-badge';
      cover.append(popular);
    }

    const brand = setText(document.createElement('span'), 'SECRET LEVEL TRIVIA');
    brand.className = 'slt-public-cover__brand';
    cover.append(brand);

    const copy = document.createElement('span');
    copy.className = 'slt-public-cover__copy';
    const category = setText(document.createElement('small'), categoryFor(item));
    const title = setText(document.createElement('strong'), item.topicName || item.title);
    const difficulty = setText(document.createElement('span'), item.difficulty || item.gameStyle || 'Ready to play');
    copy.append(category, title, difficulty);
    cover.append(copy);
    return cover;
  };

  const updateRoundMetadata = (item) => {
    const title = `${item.topicName || item.title} | Secret Level Trivia`;
    document.title = title;
    let description = document.querySelector('meta[name="description"]');
    if (!description) {
      description = document.createElement('meta');
      description.name = 'description';
      document.head.append(description);
    }
    description.content = String(item.description || `Preview ${item.topicName || item.title}, a ready-to-host Secret Level Trivia round with live scoring and musical clues.`).replace(/\s+/g, ' ').trim().slice(0, 160);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.append(canonical);
    }
    canonical.href = new URL(roundUrl(item), window.location.origin).href;
  };

  const clearRoundMetadata = () => {
    document.title = document.title.replace(/ \| Secret Level Trivia$/, '') || 'Trivia Library | Secret Level Trivia';
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.href = window.location.href.split('?')[0];
  };

  const openDetail = (item, { updateUrl = true } = {}) => {
    if (updateUrl) window.history.pushState({ round: item.slug || item.id }, '', roundUrl(item));
    updateRoundMetadata(item);
    detail.replaceChildren();
    const layout = document.createElement('div');
    layout.className = 'slt-public-detail';
    layout.append(makeCover(item, 'vertical', true));

    const copy = document.createElement('div');
    copy.className = 'slt-public-detail__copy';
    const eyebrow = setText(document.createElement('p'), categoryFor(item));
    eyebrow.className = 'slt-kicker';
    const heading = setText(document.createElement('h2'), item.topicName || item.title);
    heading.id = 'SLTCatalogDialogTitle';

    const metadata = document.createElement('div');
    metadata.className = 'slt-public-detail__meta';
    [item.difficulty || 'Mixed difficulty', item.gameStyle || 'Trivia', '1 Game Credit'].forEach((value) => metadata.append(setText(document.createElement('span'), value)));

    const description = document.createElement('div');
    description.className = 'slt-public-detail__description';
    const paragraphs = String(item.description || 'A complete Secret Level Trivia experience, ready for your next game night.').split(/\n\s*\n/).filter(Boolean);
    paragraphs.forEach((paragraph) => description.append(setText(document.createElement('p'), paragraph.trim())));

    const action = document.createElement('a');
    action.className = 'slt-button';
    action.href = 'https://host.secretleveltrivia.com';
    action.textContent = 'Sign in to host this round';
    copy.append(eyebrow, heading, metadata, description, action);
    layout.append(copy);
    detail.append(layout);

    const structuredData = document.createElement('script');
    structuredData.type = 'application/ld+json';
    structuredData.dataset.catalogStructuredData = 'true';
    structuredData.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CreativeWork',
      name: item.topicName || item.title,
      description: item.description || undefined,
      url: new URL(roundUrl(item), window.location.origin).href,
      isPartOf: { '@type': 'CollectionPage', name: 'Trivia Library', url: `${window.location.origin}/pages/browse-trivia` },
      genre: categoryFor(item),
    });
    document.head.querySelectorAll('[data-catalog-structured-data]').forEach((node) => node.remove());
    document.head.append(structuredData);

    const related = items.filter((candidate) => candidate.id !== item.id && categoryFor(candidate) === categoryFor(item)).slice(0, 5);
    if (related.length) detail.append(makeShelf('You Might Also Like', `More ${categoryFor(item).toLowerCase()} rounds.`, related));
    dialog.showModal();
  };

  const makeCard = (item) => {
    const card = document.createElement('a');
    card.className = 'slt-public-card';
    card.href = roundUrl(item);
    card.setAttribute('aria-label', `Preview ${item.topicName || item.title}${item.isPopular ? ', Popular' : ''}`);
    card.append(makeCover(item));
    const info = document.createElement('span');
    info.className = 'slt-public-card__info';
    info.append(
      setText(document.createElement('strong'), item.topicName || item.title),
      setText(document.createElement('small'), `${item.difficulty || item.gameStyle || 'Trivia'} · 1 Game Credit`),
    );
    card.append(info);
    card.addEventListener('click', (event) => {
      event.preventDefault();
      openDetail(item);
    });
    return card;
  };

  const makeShelf = (title, subtitle, shelfItems) => {
    if (!shelfItems.length) return null;
    const section = document.createElement('section');
    section.className = 'slt-public-shelf';
    const header = document.createElement('header');
    const heading = document.createElement('div');
    heading.append(setText(document.createElement('h3'), title), setText(document.createElement('p'), subtitle));
    const count = setText(document.createElement('span'), `${shelfItems.length} round${shelfItems.length === 1 ? '' : 's'}`);
    header.append(heading, count);
    const track = document.createElement('div');
    track.className = 'slt-public-shelf__track';
    shelfItems.forEach((item) => track.append(makeCard(item)));
    section.append(header, track);
    return section;
  };

  const appendShelf = (title, subtitle, shelfItems) => {
    const shelf = makeShelf(title, subtitle, shelfItems);
    if (shelf) shelves.append(shelf);
  };

  const renderNavigation = () => {
    navigation.replaceChildren();
    [['home', 'Home'], ['general', 'General Trivia'], ['themed', 'Themed Trivia']].forEach(([section, label]) => {
      const button = setText(document.createElement('button'), label);
      button.type = 'button';
      button.className = section === activeSection ? 'active' : '';
      button.setAttribute('aria-current', section === activeSection ? 'page' : 'false');
      button.addEventListener('click', () => {
        activeSection = section;
        searchInput.value = '';
        renderNavigation();
        renderCatalog();
      });
      navigation.append(button);
    });
  };

  const renderFeature = (item) => {
    feature.replaceChildren();
    if (!item) {
      feature.hidden = true;
      return;
    }
    feature.hidden = false;
    const button = document.createElement('button');
    button.type = 'button';
    button.setAttribute('aria-label', `Preview featured round: ${item.topicName || item.title}`);
    button.append(makeCover(item, 'horizontal', true));
    const copy = document.createElement('span');
    copy.className = 'slt-public-feature__copy';
    const eyebrow = setText(document.createElement('small'), item.isPopular ? 'POPULAR · NEW RELEASE' : 'NEW RELEASE');
    const title = setText(document.createElement('strong'), item.topicName || item.title);
    const description = setText(document.createElement('p'), item.description || 'A complete Secret Level Trivia experience, ready for your next game night.');
    const meta = setText(document.createElement('span'), `${item.difficulty || 'All players'} · ${item.gameStyle || 'Trivia'} · 1 Game Credit`);
    const action = setText(document.createElement('em'), 'Preview round →');
    copy.append(eyebrow, title, description, meta, action);
    button.append(copy);
    button.addEventListener('click', () => openDetail(item));
    feature.append(button);
  };

  const stopFeatureRotation = () => {
    if (!featureTimer) return;
    window.clearInterval(featureTimer);
    featureTimer = null;
  };

  const showCurrentFeature = () => {
    renderFeature(featureItems[featureIndex] || null);
  };

  const startFeatureRotation = () => {
    stopFeatureRotation();
    if (featureItems.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    featureTimer = window.setInterval(() => {
      if (featurePaused || document.hidden || dialog.open) return;
      featureIndex = (featureIndex + 1) % featureItems.length;
      showCurrentFeature();
    }, featureInterval);
  };

  const setFeatureItems = (candidates) => {
    const candidateIds = candidates.map((item) => item.id).join('|');
    const existingIds = featureItems.map((item) => item.id).join('|');
    featureItems = candidates;
    if (!featureItems.length) {
      featureIndex = 0;
      stopFeatureRotation();
      renderFeature(null);
      return;
    }
    if (candidateIds !== existingIds) featureIndex = Math.floor(Math.random() * featureItems.length);
    featureIndex %= featureItems.length;
    showCurrentFeature();
    startFeatureRotation();
  };

  const renderCatalog = () => {
    const query = searchInput.value.trim().toLowerCase();
    const scoped = sectionItems(activeSection);
    const visible = scoped.filter((item) => !query || searchableText(item).includes(query));
    shelves.replaceChildren();
    status.hidden = visible.length > 0;
    if (!visible.length) {
      status.textContent = items.length ? 'No matching rounds. Try a broader topic or section.' : 'No public trivia previews are available yet.';
      setFeatureItems([]);
      return;
    }
    status.hidden = true;
    const newest = [...visible].sort(newestFirst);
    const themedNewest = newest.filter(isThemed);
    const nonGeneralNewest = newest.filter(isNonGeneral);
    const generalNewest = newest.filter(isGeneral);
    const popular = [...visible].filter((item) => item.isPopular).sort(popularFirst);
    const featureCandidates = !query ? (activeSection === 'general' ? generalNewest : activeSection === 'themed' ? themedNewest : nonGeneralNewest).slice(0, 10) : [];
    setFeatureItems(featureCandidates);
    if (query) {
      appendShelf('Search Results', `${visible.length} matching round${visible.length === 1 ? '' : 's'}.`, newest);
      return;
    }
    if (activeSection === 'home') {
      appendShelf('New Releases', 'Fresh themed rounds ready for their first game night.', nonGeneralNewest.slice(0, 10));
      appendShelf('Popular with Hosts', 'Rounds saved by multiple hosting accounts.', popular.slice(0, 10));
      appendShelf('General Trivia', 'Fresh daily rounds for a quick game night.', generalNewest.slice(0, 12));
      return;
    }
    if (activeSection === 'general') {
      appendShelf('Latest General Trivia', 'Fresh daily rounds for a quick game night.', generalNewest.slice(0, 30));
      return;
    }
    appendShelf('New Themed Releases', 'Fresh themed rounds ready for their first game night.', themedNewest.slice(0, 10));
    [...new Set(themedNewest.map(categoryFor).sort())].forEach((category) => {
      appendShelf(category, `Explore ${category.toLowerCase()} rounds.`, themedNewest.filter((item) => categoryFor(item) === category).slice(0, 12));
    });
  };

  const closeDetail = () => {
    if (dialog.open) dialog.close();
    if (window.location.search) window.history.pushState({}, '', window.location.pathname);
    clearRoundMetadata();
    document.head.querySelectorAll('[data-catalog-structured-data]').forEach((node) => node.remove());
  };
  closeButton.addEventListener('click', closeDetail);
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) closeDetail();
  });
  window.addEventListener('popstate', () => {
    const requested = new URLSearchParams(window.location.search).get('round');
    const item = items.find((candidate) => String(candidate.slug || candidate.id) === requested);
    if (item) openDetail(item, { updateUrl: false });
    else if (dialog.open) closeDetail();
  });
  searchInput.addEventListener('input', renderCatalog);
  feature.addEventListener('mouseenter', () => { featurePaused = true; });
  feature.addEventListener('mouseleave', () => { featurePaused = false; });
  feature.addEventListener('focusin', () => { featurePaused = true; });
  feature.addEventListener('focusout', () => { featurePaused = false; });

  fetch(endpoint, { headers: { Accept: 'application/json' } })
    .then((response) => {
      if (!response.ok) throw new Error('Catalog request failed');
      return response.json();
    })
    .then((payload) => {
      items = Array.isArray(payload.items) ? payload.items : [];
      renderNavigation();
      renderCatalog();
      const requested = new URLSearchParams(window.location.search).get('round');
      const requestedItem = items.find((item) => String(item.slug || item.id) === requested);
      if (requestedItem) openDetail(requestedItem, { updateUrl: false });
    })
    .catch(() => {
      status.textContent = 'The live library is temporarily unavailable. Open Host Hub to browse after signing in.';
      status.hidden = false;
    });
})();
